import { Injectable, OnDestroy } from '@angular/core';
import {
  AbstractControl, AsyncValidatorFn, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators
} from '@angular/forms';
import {
  BehaviorSubject,
  filter,
  map, merge, Observable, of, ReplaySubject, Subject, take, takeUntil, tap, timer
} from 'rxjs';
import { PaymentTypes } from '@sondermind/features/public-facing/session-request';
import { IState, StatesServedStore } from '@sondermind/data-access/public-facing/states-served';
import { ProviderNetworkCompanyStore } from '@sondermind/data-access/provider-network-company';
import { IInsurance } from '@sondermind/utilities/models-provider-network-company';
import { DropdownOption } from '@sondermind/iris/dropdown';
import { ILocationForm } from '../models/location-form';

/**
 * Service that manages payment and location form controls.
 *
 * This service is intentionally not provided at the root level to ensure proper lifecycle
 * management. When provided at the component level, Angular will call ngOnDestroy when
 * the providing component is destroyed, allowing us to clean up subscriptions and prevent
 * memory leaks.
 *
 * Usage:
 * 1. Add PaymentLocationService to the providers array of your component
 * 2. Inject it in your component constructor
 * 3. Access form controls and observables as needed
 */
@Injectable()
export class PaymentLocationService implements OnDestroy {
  readonly locationControl: FormGroup<ILocationForm>;
  readonly paymentTypeControl: FormControl<PaymentTypes | null>;
  readonly insuranceControl: FormControl<number | null>;
  private readonly UNSUPPORTED_PSYCH_INSURANCE_VALUE = -1;

  private locationSelectionComplete$ = new Subject<void>();
  private lastSelectedAddress: string | null = null;
  private allInsurancesInState: Record<PaymentTypes, IInsurance[]> = {
    [PaymentTypes.INSURANCE]: [],
    [PaymentTypes.EAP]: [],
    [PaymentTypes.MEDICARE]: [],
    [PaymentTypes.MEDICAID]: [],
    [PaymentTypes.CASH]: []
  };
  private medicationManagementChecked: boolean = false;
  private acceptedStates$ = new BehaviorSubject<IState[]>([]);
  private availableInYourState$ = new ReplaySubject<boolean>(1);
  private showInsurancePlans$ = new BehaviorSubject<boolean>(false);
  private paymentTypesInState$ = new BehaviorSubject<Array<DropdownOption<string>>>([]);
  private acceptedInsurances$ = new BehaviorSubject<Array<DropdownOption<number>>>([]);

  private destroyed$ = new Subject<void>();

  constructor(
    private pncStore: ProviderNetworkCompanyStore,
    private statesServedStore: StatesServedStore,
  ) {
    // Initialize form controls
    this.locationControl = this.initializeLocationControl();
    this.paymentTypeControl = this.initializePaymentTypeControl();
    this.insuranceControl = this.initializeInsuranceControl();

    // Setup initial state
    this.loadStates();
    this.setupStateChangeSubscription();
    this.setupPaymentTypeChangeSubscription();
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  getAvailableInYourState$(): Observable<boolean> {
    return this.availableInYourState$.asObservable();
  }

  getPaymentTypesInState$(): Observable<Array<DropdownOption<string>>> {
    return this.paymentTypesInState$;
  }

  getAcceptedInsurances$(): Observable<Array<DropdownOption<number>>> {
    return this.acceptedInsurances$;
  }

  getShowInsurancePlans$(): Observable<boolean> {
    return this.showInsurancePlans$;
  }

  setMedicationManagement(value: boolean): void {
    this.medicationManagementChecked = value;

    // Reset the insurance selection if:
    // 1. Medication management is being turned OFF
    // 2. The current insurance value is our special marker for unsupported psychiatric coverage
    // This prevents users from keeping an invalid insurance selection when they no longer need psychiatric coverage
    if (!value && this.insuranceControl.value === this.UNSUPPORTED_PSYCH_INSURANCE_VALUE) {
      this.insuranceControl.setValue(null);
    }

    // Re-validate the insurance selection whenever medication management status changes
    // This ensures the validInsuranceValidator runs to:
    // - Add an error if medication management is ON and selected insurance doesn't support it
    // - Remove any existing errors if medication management is OFF
    this.insuranceControl.updateValueAndValidity();

    // if location is selected, update the insurance options
    const state = this.locationControl.controls.state.value;
    if (state) {
      this.getInsuranceOptionsByState(state);
    }

    // If payment type is selected, update the accepted insurance options
    const paymentType = this.paymentTypeControl.value;
    if (paymentType) {
      this.updateAcceptedInsurances(paymentType);
    }
  }

  private initializeLocationControl(): FormGroup<ILocationForm> {
    return new FormGroup<ILocationForm>({
      name: new FormControl<string | null>(null, [Validators.required]),
      lat: new FormControl<number | null>(null, [Validators.required]),
      lng: new FormControl<number | null>(null, [Validators.required]),
      address: new FormControl<string | null>(
        null,
        {
          validators: [Validators.required],
          asyncValidators: [this.selectedLocationValidator()],
          updateOn: 'blur'
        }
      ),
      state: new FormControl<string | null>(null, [Validators.required]),
      zip: new FormControl<string | null>(null)
    });
  }

  private initializePaymentTypeControl(): FormControl<PaymentTypes | null> {
    return new FormControl<PaymentTypes | null>(
      { value: null, disabled: true },
      [Validators.required]
    );
  }

  private initializeInsuranceControl(): FormControl<number | null> {
    return new FormControl<number | null>(
      { value: null, disabled: true },
      [Validators.required, this.insuranceValidator()]
    );
  }

  private selectedLocationValidator(): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      // If no value, let required validator handle it
      if (!control.value) {
        return of(null);
      }

      // Wait briefly for potential selection to complete
      return merge(
        // Wait for selection complete event
        this.locationSelectionComplete$.pipe(
          take(1),
          map(() => null) // Valid if selection completed
        ),
        // Or timeout after 300ms and validate normally
        timer(300).pipe(
          map(() => {
            if (control.value === this.lastSelectedAddress) return null;
            this.resetPaymentTypeAndInsurance(true);
            return { invalidLocation: true };
          })
        )
      ).pipe(
        take(1) // Take first response (either selection complete or timeout)
      );
    };
  }

  private insuranceValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null;

      if (!this.medicationManagementChecked) return null;

      const insurance = this.allInsurancesInState[this.paymentTypeControl.value ?? PaymentTypes.INSURANCE]
        .find((x) => x.pncId === control.value);

      if (!insurance) return null;
      if (insurance.includesPsych) return null;

      // Instead of setting to null (which triggers required validator),
      // set to a special value that indicates invalid psych insurance
      control.setValue(this.UNSUPPORTED_PSYCH_INSURANCE_VALUE, { emitEvent: false });
      return { hasUnsupportedPsychInsurance: true };
    };
  }

  locationSelected(selectedAddress: google.maps.places.PlaceResult): void {
    if (!(selectedAddress?.geometry && selectedAddress?.formatted_address && selectedAddress?.name)) return;

    const state = selectedAddress?.address_components?.find(
      (x) => x.types.includes('administrative_area_level_1'))?.short_name;
    if (!state) return;

    const zip = selectedAddress?.address_components?.find(
      (x) => x?.types?.includes('postal_code'))?.long_name;

    this.lastSelectedAddress = selectedAddress.formatted_address;

    this.locationControl.patchValue({
      name: selectedAddress.name,
      lat: selectedAddress.geometry?.location?.lat(),
      lng: selectedAddress.geometry?.location?.lng(),
      address: selectedAddress.formatted_address,
      state: state ?? '',
      zip: zip ?? null
    });

    this.locationSelectionComplete$.next();
  }

  setupStateChangeSubscription(): void {
    this.locationControl?.controls?.state.valueChanges.pipe(
      takeUntil(this.destroyed$),
      filter((state) => !!state),
      tap((state) => {
        const isValidState = this.checkIfStateIsValid(state ?? '');
        this.updateFormControlsBasedOnStateAvailability(isValidState);
        this.resetPaymentTypeAndInsurance(!isValidState);
        this.getInsuranceOptionsByState(state ?? '');
      })
    ).subscribe();
  }

  setupPaymentTypeChangeSubscription(): void {
    this.paymentTypeControl.valueChanges.pipe(
      takeUntil(this.destroyed$),
      filter((paymentType) => !!paymentType),
      tap((paymentType) => {
        this.updateAcceptedInsurances(paymentType);
        this.updateInsuranceBasedOnPaymentType(paymentType);
      })
    ).subscribe();
  }

  private loadStates(): void {
    this.statesServedStore.stateList$().subscribe({
      next: (states) => {
        this.acceptedStates$.next(states);
      }
    });
  }

  private checkIfStateIsValid(state: string): boolean {
    const isValidState = this.acceptedStates$?.value?.some((x) => x.abbreviation === state);
    this.availableInYourState$.next(isValidState);
    return isValidState;
  }

  private getInsuranceOptionsByState(state: string): void {
    this.pncStore.getInsuranceOptionsByState$({
      state,
      forceReload: false,
      includeCashPay: true
    }).pipe(
      tap((insurances) => {
        this.categorizeInsurances(insurances);
        this.updatePaymentTypes();
      })
    ).subscribe();
  }

  private categorizeInsurances(insurances: IInsurance[]): void {
    // Reset all categories
    Object.keys(this.allInsurancesInState).forEach((key) => {
      this.allInsurancesInState[key as PaymentTypes] = [];
    });

    // Filter out insurances that don't support psychiatry when medication management is checked
    const filteredInsurances = this.medicationManagementChecked
      ? insurances.filter((insurance) => insurance.includesPsych || insurance.isCashPay)
      : insurances;

    // Categorize each insurance by payment type
    filteredInsurances.forEach((insurance) => {
      if (insurance.isPrivateInsurance) {
        this.allInsurancesInState[PaymentTypes.INSURANCE].push(insurance);
      }
      if (insurance.isEap) {
        this.allInsurancesInState[PaymentTypes.EAP].push(insurance);
      }
      if (insurance.isMedicare) {
        this.allInsurancesInState[PaymentTypes.MEDICARE].push(insurance);
      }
      if (insurance.isMedicaid) {
        this.allInsurancesInState[PaymentTypes.MEDICAID].push(insurance);
      }
      if (insurance.isCashPay) {
        this.allInsurancesInState[PaymentTypes.CASH].push(insurance);
      }
    });
  }

  private updatePaymentTypes(): void {
    const paymentTypes = Object.keys(this.allInsurancesInState)
      .filter((key) => this.allInsurancesInState[key as PaymentTypes].length > 0)
      .map((key) => ({ name: key, value: key }));

    this.paymentTypesInState$.next(paymentTypes);

    const currentPaymentType = this.paymentTypeControl.value;
    if (currentPaymentType) {
      const isCurrentTypeValid = paymentTypes.some((pt) => pt.value === currentPaymentType);
      if (isCurrentTypeValid) return;
      this.paymentTypeControl.setValue(null, { emitEvent: false });
      this.acceptedInsurances$.next([]);
    }
  }

  private updateAcceptedInsurances(paymentType: PaymentTypes): void {
    const insurances = this.allInsurancesInState[paymentType] || [];

    const acceptedInsurances = insurances.map((insurance) => ({
      name: insurance.title,
      value: insurance.pncId
    }));

    this.acceptedInsurances$.next(acceptedInsurances);
  }

  private updateInsuranceBasedOnPaymentType(paymentType: PaymentTypes): void {
    const acceptedInsurances = this.acceptedInsurances$.value;

    // For cash payment, auto-select the self-pay insurance option
    // For other payment types, clear the insurance selection
    const insurancePncId = paymentType === PaymentTypes.CASH
      ? acceptedInsurances?.[0]?.value ?? null
      : null;

    if (insurancePncId !== this.insuranceControl.value) {
      this.insuranceControl.patchValue(insurancePncId);
    }

    // Only show insurance selection for non-cash payment types that have options
    const shouldShowInsurancePlans = acceptedInsurances.length > 0 && paymentType !== PaymentTypes.CASH;
    this.showInsurancePlans$.next(shouldShowInsurancePlans);
  }

  private resetPaymentTypeAndInsurance(disablePaymentAndInsurance: boolean = true): void {
    if (disablePaymentAndInsurance) {
      this.paymentTypeControl.disable({ emitEvent: false });
      this.insuranceControl.disable({ emitEvent: false });
    }

    this.paymentTypeControl.patchValue(null);
    this.insuranceControl.patchValue(null);
    this.showInsurancePlans$.next(false);
  }

  private updateFormControlsBasedOnStateAvailability(isValidState: boolean) {
    const form = this.locationControl.parent;
    if (!form) return;

    if (isValidState) {
      form.enable({ emitEvent: false });
    } else {
      form.disable({ emitEvent: false });
      this.locationControl.enable({ emitEvent: false });
    }
  }
}
